import React, { useEffect, useState } from 'react';
import { useApi, microsoftAuthApiRef } from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * Custom scaffolder field extension that automatically acquires a Microsoft
 * access token scoped to Azure Management API.
 *
 * This is a hidden field — it renders no visible UI. When included in a
 * scaffolder template, it silently obtains the user's Azure management token
 * and passes it as input to the scaffolder action. The action then uses this
 * token with the On-Behalf-Of (OBO) flow to provision Azure resources under
 * the user's identity and RBAC permissions.
 *
 * Usage in template.yaml:
 * ```yaml
 * parameters:
 *   - title: Azure Configuration
 *     properties:
 *       userToken:
 *         type: string
 *         ui:field: AzureTokenField
 * ```
 */
export const AzureTokenField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, formData } = props;
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const [error, setError] = useState<string>();

  useEffect(() => {
    // Only fetch token once (when formData is empty)
    if (formData) return;

    microsoftAuth
      .getAccessToken('https://management.azure.com/user_impersonation')
      .then(token => {
        onChange(token);
        setError(undefined);
      })
      .catch(err => {
        setError(`Failed to acquire Azure token: ${err.message}`);
      });
  }, [microsoftAuth, onChange, formData]);

  if (error) {
    return (
      <div style={{ color: '#f44336', fontSize: '0.875rem', marginTop: 4 }}>
        {error}
      </div>
    );
  }

  // Hidden field — no visible UI when token is acquired successfully
  return null;
};
